import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { Button } from '@tempmee/react';
import { Box } from '@tempmee/react';
import ConfirmPopup from '~/components/shared/ConfirmPopup';
import { FormActions, FormErrorBanner, useScrollToError } from '~/components/shared/forms';
import type { IShiftDetailsFormValues } from '~/components/ShiftRequestRedesign/ShiftDetails/ShiftDetailsForm/useShiftDetailsForm';
import { UserProfession, UserSubProfession } from '~/enums/UserProfession';
import { useUserSelector } from '~/hooks';
import { useNavigationDetector } from '~/hooks/useNavigationDetector';
import { fireDatesSelected, fireSelectDatesPageViewed } from '~/posthogs/events';
import { ShiftRequestHeader, WizardNavigationGuard } from '../common';
import { DatesCard } from './DatesCard';
import { useSafeSelectDatesLocationState } from './useSafeLocationState';
import { useSelectDates } from './useSelectDates';
import { checkIfAnyHoliday, convertDatesToTimezone } from './utils';

export const SelectDatesScreen = () => {
  const navigate = useNavigate();
  const { setValue, getValues, formState } = useFormContext<IShiftDetailsFormValues>();
  const user = useUserSelector();
  const [showHolidayWarning, setShowHolidayWarning] = useState(false);
  const [showDateError, setShowDateError] = useState(false);
  const bannerRef = useScrollToError(showDateError);

  const {
    professionType: navProfessionType,
    preselectedDate,
    professionals,
    inviteSource,
  } = useSafeSelectDatesLocationState();

  // Resolve profession type code to display name (e.g. 'RDH' -> 'Dental Hygienist')
  const resolvedProfessionType = navProfessionType
    ? UserProfession.getName(navProfessionType)
      || UserSubProfession.getName(navProfessionType)
      || navProfessionType
    : getValues('professionType') || '';

  // Set professionType in form on mount when coming from PostNewShiftsButton
  useEffect(() => {
    if (navProfessionType) {
      const resolved =
        UserProfession.getName(navProfessionType)
        || UserSubProfession.getName(navProfessionType)
        || navProfessionType;
      setValue('professionType', resolved);
    }
  }, [navProfessionType, setValue]);

  // Set inviteSource in form from navigation state
  useEffect(() => {
    if (inviteSource) {
      setValue('inviteSource', inviteSource);
    }
  }, [inviteSource, setValue]);

  // Set invite field when professionals are passed from navigation state
  useEffect(() => {
    if (professionals?.length) {
      const ids: string[] = [];
      const names: Record<string, string> = {};

      professionals.forEach((p) => {
        if (typeof p === 'string') {
          ids.push(p);
        } else {
          ids.push(p.id);
          if (p.name) {
            names[p.id] = p.name;
          }
        }
      });

      setValue('invite', ids);
      if (Object.keys(names).length > 0) {
        setValue('inviteNames', names);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- Run once on mount: seed invite list from navigation state

  // Pre-select existing dates from form context when navigating back from ShiftDetails
  const existingDates = getValues('dates');

  const {
    selectedDates,
    handleCalendarSelect: hookCalendarSelect,
    handleRemoveDate,
    canContinue,
    tipVisible,
    nextButtonText,
  } = useSelectDates({ initialDate: preselectedDate, initialDates: existingDates });

  const handleCalendarSelect = useCallback(
    (dates: Date[] | undefined) => {
      if (!dates) {
        hookCalendarSelect([]);
        return;
      }
      hookCalendarSelect(dates.map((d) => format(d, 'yyyy-MM-dd')));
    },
    [hookCalendarSelect],
  );

  useEffect(() => {
    fireSelectDatesPageViewed();
  }, []);

  const hasChanges = selectedDates.length > 0 || formState.isDirty;
  const [showNavigationGuard, setShowNavigationGuard] = useState(false);

  const onNavigationAttempt = useCallback(() => {
    setShowNavigationGuard(true);
  }, []);

  const { allowNavigation, navigateToPending, clearPendingNavigation } = useNavigationDetector({
    when: hasChanges,
    onNavigationAttempt,
  });

  const proceedToShiftDetails = () => {
    fireDatesSelected();
    allowNavigation();
    setValue('dates', selectedDates);
    setValue('professionType', resolvedProfessionType);
    navigate('/dashboard/multi-day/info');
  };

  // Auto-clear the date error once the user selects at least one date
  useEffect(() => {
    if (canContinue && showDateError) {
      setShowDateError(false);
    }
  }, [canContinue, showDateError]);

  const handleNext = () => {
    if (!canContinue) {
      setShowDateError(true);
      return;
    }
    const dateStrings = selectedDates.map((d) => format(d, 'yyyy-MM-dd'));
    if (checkIfAnyHoliday(convertDatesToTimezone(dateStrings, user.time_zone))) {
      setShowHolidayWarning(true);
      return;
    }
    proceedToShiftDetails();
  };

  return (
    <ShiftRequestHeader currentStep={0} backUrl="/" maxWidth={1120}>
      <Box>
        <FormErrorBanner
          ref={bannerRef}
          visible={showDateError}
          message="Please select at least one date to continue."
        />
      </Box>

      <DatesCard
        selectedDates={selectedDates}
        onCalendarSelect={handleCalendarSelect}
        onRemoveDate={handleRemoveDate}
        hasError={showDateError}
        tipVisible={tipVisible}
      />

      <FormActions>
        <Button
          data-testid="select-dates-button"
          data-cy="select-dates-button"
          onClick={handleNext}
        >
          {nextButtonText}
        </Button>
      </FormActions>

      {showHolidayWarning && (
        // @ts-expect-error -- ConfirmPopup still written in JavaScript
        <ConfirmPopup
          title="Major U.S Holiday Conflict"
          description="Looks like you are trying to post a shift on a Major U.S. Holiday. Please make sure your office will be open on this date!"
          leftButtonText="Go Back"
          leftButtonAction={() => setShowHolidayWarning(false)}
          rightButtonText="Continue"
          rightButtonAction={() => {
            setShowHolidayWarning(false);
            proceedToShiftDetails();
          }}
          closePopup={() => setShowHolidayWarning(false)}
          dataTestId="holiday-warning-popup"
        />
      )}

      <WizardNavigationGuard
        isOpen={showNavigationGuard}
        onStay={() => {
          clearPendingNavigation();
          setShowNavigationGuard(false);
        }}
        onLeave={navigateToPending}
      />
    </ShiftRequestHeader>
  );
};
